// Banner unter PageHeading, sobald eine Ressource ab 80 % belegt ist. Nur in
// enforced, Bestandskunden und preview sehen nichts. Bei warning lässt er sich
// für die Session ausblenden, bei voll und darüber bleibt er stehen.
// Details öffnet den Limit-Dialog mit allen Optionen (archivieren, anfragen,
// vergleichen), deshalb hier nur Details und der Link zum Paketvergleich.
import { ArrowTopRightOnSquareIcon } from '@heroicons/react/16/solid'
import PropTypes from 'prop-types'
import { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { usePartnerContext } from '../../context'
import { MODES } from '../../features/pricing/entitlements'
import { useEntitlements, useLimit } from '../../features/pricing/hooks'
import { LIMIT_STATES } from '../../features/pricing/limits'
import { usePricingDialog } from '../../features/pricing/pricing-dialog-context'
import { getPricingCompareUrl } from '../../features/pricing/use-feature-gate'
import { classNames } from '../../helpers'
import { getFocusTargetAfterRemoval, isFocusVisible } from '../../helpers/focus'
import InfoCallout from '../tailwind/InfoCallout'
import { formatCount } from './usage-meter'

// Ressource im Code und im i18n-Pfad
const I18N_RESOURCE = {
  careers: 'careers',
  seats: 'seats',
  talentStorage: 'talent_storage'
}

const BANNER_STATES = [
  LIMIT_STATES.WARNING,
  LIMIT_STATES.AT_LIMIT,
  LIMIT_STATES.OVER_LIMIT
]

const DISMISS_PREFIX = 'PRICING_USAGE_BANNER_DISMISSED'

// sessionStorage kann in privaten Fenstern oder bei gesperrten Website-Daten
// werfen. Dann bleibt das Ausblenden im Speicher der Seite.
const readDismissed = (key) => {
  try {
    return window.sessionStorage.getItem(`${DISMISS_PREFIX}:${key}`) === '1'
  } catch {
    return false
  }
}

const writeDismissed = (key) => {
  try {
    window.sessionStorage.setItem(`${DISMISS_PREFIX}:${key}`, '1')
  } catch {
    // bewusst leer, siehe readDismissed
  }
}

/**
 * Text des Banners, rein und ohne React. Plural-Keys bekommen count als Zahl,
 * i18next formatiert sie selbst. Alle anderen Zahlen kommen formatiert.
 *
 * @param {Function} t
 * @param {{ resource: string, state: string, used: number, limit: number,
 *   ratio?: number|null, language?: string }} params
 * @returns {string|null}
 */
export const usageBannerText = (
  t,
  { resource, state, used, limit, ratio = null, language }
) => {
  const name = I18N_RESOURCE[resource]
  if (!name || !BANNER_STATES.includes(state)) return null

  const key = `pricing.banner.${name}_${state}`
  const format = (value) => formatCount(value, language)

  if (resource === 'talentStorage') {
    if (state === LIMIT_STATES.WARNING) {
      const share = ratio ?? (limit > 0 ? used / limit : 0)
      return t(key, {
        percent: Math.floor(share * 100),
        used: format(used),
        limit: format(limit)
      })
    }
    if (state === LIMIT_STATES.AT_LIMIT) return t(key)
    return t(key, {
      count: Math.max(0, used - limit),
      limit: format(limit)
    })
  }

  if (state === LIMIT_STATES.WARNING) {
    return t(key, { used: format(used), limit: format(limit) })
  }
  if (state === LIMIT_STATES.AT_LIMIT) return t(key, { count: limit })
  return t(key, { used: format(used), count: limit })
}

const ACTION_STYLES = {
  warning:
    'text-yellow-800 hover:text-yellow-900 focus-visible:outline-yellow-700',
  critical: 'text-red-800 hover:text-red-900 focus-visible:outline-red-700'
}

const UsageBanner = ({ resource, source, className }) => {
  const { t, i18n } = useTranslation()
  const { mode } = useEntitlements()
  const usage = useLimit(resource)
  const { partner } = usePartnerContext()
  const dialog = usePricingDialog()
  const wrapperRef = useRef(null)

  // Ein neues Limit zeigt den Hinweis wieder
  const dismissKey = `${partner?.id}:${resource}:${usage.limit}`
  const [dismissedKey, setDismissedKey] = useState(null)

  const visible =
    mode === MODES.ENFORCED &&
    usage.enforced &&
    BANNER_STATES.includes(usage.state)
  if (!visible) return null

  const sticky = usage.state !== LIMIT_STATES.WARNING
  const isDismissed = dismissedKey === dismissKey || readDismissed(dismissKey)
  if (!sticky && isDismissed) return null

  const text = usageBannerText(t, {
    resource,
    state: usage.state,
    used: usage.used,
    limit: usage.limit,
    ratio: usage.ratio,
    language: i18n?.language
  })
  if (!text) return null

  const tone = sticky ? 'critical' : 'warning'
  const actionClass = classNames(
    'inline-flex items-center gap-x-1 whitespace-nowrap rounded font-semibold underline-offset-2 hover:underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2',
    ACTION_STYLES[tone]
  )

  const handleDismiss = (event) => {
    // Per Tastatur geschlossen geht der Fokus zum nächsten Bedienelement statt
    // an den Seitenanfang, wie beim Beispieldaten-Hinweis
    const next = isFocusVisible(event?.currentTarget)
      ? getFocusTargetAfterRemoval(wrapperRef.current)
      : null

    writeDismissed(dismissKey)
    setDismissedKey(dismissKey)
    next?.focus()
  }

  return (
    <div ref={wrapperRef} className={className} data-resource={resource}>
      <InfoCallout
        tone={tone}
        onDismiss={sticky ? undefined : handleDismiss}
        dismissLabel={sticky ? undefined : t('pricing.banner.dismiss_action')}
        action={
          <div className='flex flex-wrap items-center gap-x-4 gap-y-2'>
            <button
              type='button'
              aria-haspopup='dialog'
              onClick={() =>
                dialog?.openLimitDialog?.(resource, { source, action: 'info' })
              }
              className={actionClass}
            >
              {t('pricing.banner.details_action')}
            </button>
            <a
              href={getPricingCompareUrl(i18n?.language)}
              target='_blank'
              rel='noopener noreferrer'
              className={actionClass}
            >
              {t('pricing.banner.compare_action')}
              <ArrowTopRightOnSquareIcon
                aria-hidden='true'
                className='size-3.5'
              />
            </a>
          </div>
        }
      >
        {text}
      </InfoCallout>
    </div>
  )
}

UsageBanner.propTypes = {
  resource: PropTypes.oneOf(['careers', 'seats', 'talentStorage']).isRequired,
  source: PropTypes.string,
  className: PropTypes.string
}

UsageBanner.defaultProps = {
  source: undefined,
  className: undefined
}

export default UsageBanner
